import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
// Firebase
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { firebaseApp } from "../firebase";

export interface User {
  id: string;
  name: string;
  email: string;
  password?: string; // For testing purposes only
  userType: "jobseeker" | "hr" | "admin";
  company?: string;
  badge?: "silver" | "gold";
  avatar?: string;
  createdAt?: Timestamp | Date;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: any) => Promise<boolean>;
  googleSignIn: (userType?: "jobseeker" | "hr" | "admin") => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  updateUserProfile: (updates: Partial<User>) => void; // Update user profile in context
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Firebase instances
  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);

  async function getUserTypeAndProfileData(userId: string): Promise<{
    userType: "jobseeker" | "hr" | "admin";
    profileData: Partial<User> | null;
  }> {
    let userType: "jobseeker" | "hr" | "admin" = "jobseeker";
    let profileData: Partial<User> | null = null;
    try {
      // Check recruiters first to avoid false jobseeker match when both docs exist
      const recruiterSnap = await getDoc(doc(db, "recruiters", userId));
      if (recruiterSnap.exists()) {
        profileData = recruiterSnap.data() as Partial<User>;
        userType = "hr";
      } else {
        const seekerSnap = await getDoc(doc(db, "jobseekers", userId));
        if (seekerSnap.exists()) {
          profileData = seekerSnap.data() as Partial<User>;
          userType = "jobseeker";
        }
      }
    } catch (err) {
    }
    return { userType, profileData };
  }

  useEffect(() => {
    // NOTE: We no longer clear localStorage on browser close to allow persistent login
    // Users will stay logged in until they explicitly logout
    // Session tracking will still work - sessions without logoutTime are considered active

    // Set auth persistence to LOCAL so users stay logged in across browser restarts
    setPersistence(auth, browserLocalPersistence).catch(() => {});

    // Handle redirect result first (for users returning from Google OAuth redirect)
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (result?.user) {

          // Get the selected user type from localStorage if it was a signup flow
          const storedUserType = localStorage.getItem("google_signup_type") as
            | "jobseeker"
            | "hr"
            | "admin"
            | null;


          if (storedUserType) {
            // Signup flow - create new user with specified type
            localStorage.removeItem("google_signup_type");
            const success = await handleGoogleUser(result.user, storedUserType);
            if (!success) {
              return;
            }
          } else {
            // Login flow - validate existing user
            const success = await handleGoogleUser(result.user);
            if (!success) {
              return;
            }
          }

          toast.success("Successfully signed in with Google!");
        } else {

          // Check if we're returning from a redirect by looking for auth-related URL params
          const urlParams = new URLSearchParams(window.location.search);
          const hasAuthParams =
            urlParams.has("code") ||
            urlParams.has("state") ||
            window.location.hash.includes("access_token") ||
            window.location.search.includes("authuser") ||
            document.referrer.includes("accounts.google.com");

          if (hasAuthParams || document.referrer.includes("google.com")) {
            // Detected return from Google but no redirect result

            // Wait a bit and try again - sometimes there's a delay
            setTimeout(async () => {
              try {
                const retryResult = await getRedirectResult(auth);
                if (retryResult?.user) {

                  const storedUserType = localStorage.getItem(
                    "google_signup_type"
                  ) as "jobseeker" | "hr" | "admin" | null;

                  const success = await handleGoogleUser(
                    retryResult.user,
                    storedUserType || undefined
                  );
                  if (storedUserType)
                    localStorage.removeItem("google_signup_type");

                  if (success) {
                    toast.success("Successfully signed in with Google!");
                  }
                } else {
                  toast.error("Google sign-in failed. Please try again.");
                  // Clean up URL
                  window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname
                  );
                }
              } catch (retryErr) {
                toast.error("Google sign-in failed. Please try again.");
              }
            }, 1000);
          }
        }
      } catch (err) {
        toast.error("Authentication failed. Please try again.");
        setIsLoading(false);
      }
    };

    handleRedirectResult();

    // Firebase auth listener – keeps `user` in sync with real auth state
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        // Try to fetch additional profile info from role-based collections
        let { userType, profileData } = await getUserTypeAndProfileData(
          fbUser.uid
        );

        // If profile data is missing but user exists in Firebase Auth, create/update the profile
        if (!profileData && fbUser.displayName) {
          try {
            const collectionName =
              userType === "hr" ? "recruiters" : "jobseekers";
            await setDoc(
              doc(db, collectionName, fbUser.uid),
              {
                name: fbUser.displayName,
                email: fbUser.email,
                userType: userType,
                avatar: fbUser.photoURL,
                createdAt: new Date(),
              },
              { merge: true }
            );

            // Update profileData with the created data
            profileData = {
              name: fbUser.displayName,
              email: fbUser.email,
              userType: userType,
              avatar: fbUser.photoURL,
            };
          } catch (error) {
          }
        }

        // AUTOMATIC MIGRATION: Add subscription field if missing (for old users)
        if (profileData && !profileData.subscription) {
          try {
            const collectionName =
              userType === "hr" ? "recruiters" : "jobseekers";
            const subscriptionData =
              userType === "jobseeker"
                ? {
                    plan: "free",
                    status: "active",
                    startDate: new Date(),
                    expiresAt: null,
                  }
                : {
                    plan: "none",
                    status: "inactive",
                    startDate: null,
                    expiresAt: null,
                  };

            await updateDoc(doc(db, collectionName, fbUser.uid), {
              subscription: subscriptionData,
            });
            // Auto-migrated subscription for user
          } catch (error) {
            // Subscription migration failed
          }
        }

        const signedInUser: User = {
          id: fbUser.uid,
          name:
            profileData?.name ||
            fbUser.displayName ||
            fbUser.email?.split("@")[0] ||
            "",
          email: fbUser.email || profileData?.email || "", // Prioritize Firebase Auth email
          userType,
          company: profileData?.company,
          badge: profileData?.badge,
          avatar: fbUser.photoURL || undefined,
        };

        setUser(signedInUser);
        localStorage.setItem("vetriaai_user", JSON.stringify(signedInUser));
      } else {
        setUser(null);
        localStorage.removeItem("vetriaai_user");
      }
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array - only run once on mount

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const fbUser = userCredential.user;

      // After successful auth, verify user exists in our database
      const recruiterSnap = await getDoc(doc(db, "recruiters", fbUser.uid));
      const jobseekerSnap = await getDoc(doc(db, "jobseekers", fbUser.uid));

      if (!recruiterSnap.exists() && !jobseekerSnap.exists()) {
        // If they have a valid auth account but no profile, sign them out
        await signOut(auth);
        toast.error(
          "Your account is not fully set up. Please contact support or sign up again."
        );
        setIsLoading(false);
        return false;
      }

      const sessionRef = await addDoc(collection(db, "userSessions"), {
        userId: fbUser.uid,
        userEmail: fbUser.email,
        userType: (await getUserTypeAndProfileData(fbUser.uid)).userType,
        loginTime: Date.now(),
        logoutTime: null,
        sessionDuration: null,
        createdAt: new Date(),
      });
      // Store session ID for logout tracking
      localStorage.setItem("currentSessionId", sessionRef.id);
      // onAuthStateChanged will handle setting the user state, so we just return true
      return true;
    } catch (err) {
      toast.error("Invalid email or password.");
      setIsLoading(false);
      return false;
    } finally {
      // setLoading is handled in onAuthStateChanged
      setIsLoading(false);
    }
  };

  const register = async (userData: any): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const fbUser = userCredential.user;

      // Store additional profile info in Firestore
      const collName = userData.userType === "hr" ? "recruiters" : "jobseekers";
      const profileData: any = {
        name: userData.name,
        email: userData.email,
        userType: userData.userType,
        createdAt: new Date(),
      };

      // Add user type specific fields
      if (userData.userType === "jobseeker") {
        profileData.phone = userData.phone ?? null;
        profileData.location = userData.location ?? null;
      }

      if (userData.userType === "hr") {
        profileData.company = userData.company ?? null;
        profileData.companyName = userData.companyName ?? userData.company ?? null;
        profileData.companyWebsite = userData.companyWebsite ?? null;
        profileData.phone = userData.phone ?? null;
      }

      // Handle subscription data from payment (if user is paying during signup)
      if (userData.isPremium && userData.subscriptionPlan) {
        profileData.isPremium = true;
        profileData.subscriptionPlan = userData.subscriptionPlan; // "Pro" or "Core"
        profileData.subscriptionDuration = userData.subscriptionDuration;
        profileData.subscriptionAmount = userData.subscriptionAmount;
        profileData.subscriptionStartDate = userData.subscriptionStartDate;
        profileData.subscriptionExpiryDate = userData.subscriptionExpiryDate;
        profileData.paymentId = userData.paymentId;
        profileData.orderId = userData.orderId;
        profileData.paymentSignature = userData.paymentSignature;

        // Legacy subscription object for backward compatibility
        profileData.subscription = {
          plan: userData.subscriptionPlan.toLowerCase(), // "pro" or "core"
          status: "active",
          startDate: userData.subscriptionStartDate,
          expiresAt: userData.subscriptionExpiryDate,
        };
      } else {
        // Default: Free user
        profileData.isPremium = false;
        profileData.subscriptionPlan = userData.userType === "jobseeker" ? "Free" : "None";

        if (userData.userType === "jobseeker") {
          profileData.subscription = {
            plan: "free",
            status: "active",
            startDate: new Date(),
            expiresAt: null,
          };
        } else {
          profileData.subscription = {
            plan: "none",
            status: "inactive",
            startDate: null,
            expiresAt: null,
          };
        }
      }

      await setDoc(doc(db, collName, fbUser.uid), profileData);

      // onAuthStateChanged will handle setting the user state
      return true;
    } catch (err) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const sessionId = localStorage.getItem("currentSessionId");
    if (sessionId) {
      const userSessionDoc = await getDoc(doc(db, "userSessions", sessionId));
      // Update session with logout time
      const sessionRef = doc(db, "userSessions", sessionId);
      await updateDoc(sessionRef, {
        logoutTime: Date.now(),
        sessionDuration: Date.now() - (userSessionDoc.data()?.loginTime || 0), // in ms
      });

      localStorage.removeItem("currentSessionId");
    }
    await signOut(auth);
  };

  // Helper function to handle Google user creation/validation
  const handleGoogleUser = async (
    fbUser: any,
    selectedType?: "jobseeker" | "hr" | "admin"
  ) => {
    // Check if user exists in Firestore
    const recruiterSnap = await getDoc(doc(db, "recruiters", fbUser.uid));
    const jobseekerSnap = await getDoc(doc(db, "jobseekers", fbUser.uid));

    if (!recruiterSnap.exists() && !jobseekerSnap.exists()) {
      // If user does not exist but we are in SIGN-UP flow (selectedType provided), create profile
      if (selectedType) {
        const collectionName =
          selectedType === "hr" ? "recruiters" : "jobseekers";
        const newProfileData: any = {
          name: fbUser.displayName,
          email: fbUser.email,
          userType: selectedType,
          avatar: fbUser.photoURL,
          createdAt: new Date(),
        };

        // Add subscription fields for jobseekers (default: free)
        if (selectedType === "jobseeker") {
          newProfileData.subscription = {
            plan: "free", // "free" or "pro"
            status: "active",
            startDate: new Date(),
            expiresAt: null, // null for free plan
          };
        }

        // Add subscription fields for HR (default: no plan)
        if (selectedType === "hr") {
          newProfileData.subscription = {
            plan: "none", // "none" or "core"
            status: "inactive",
            startDate: null,
            expiresAt: null,
          };
        }

        await setDoc(doc(db, collectionName, fbUser.uid), newProfileData);
        return true;
      }

      // LOGIN flow and user missing ➜ block login
      await signOut(auth);
      toast.error(
        "No account found with this Google account. Please sign up first."
      );
      return false;
    }

    let resolvedType: "jobseeker" | "hr" | "admin" = recruiterSnap.exists()
      ? "hr"
      : "jobseeker";

    // If called from a signup page, ensure the role matches existing profile
    if (selectedType && selectedType !== resolvedType) {
      await signOut(auth);
      toast.error(
        `You are already registered as a ${resolvedType}. Please log in on the appropriate page.`
      );
      return false;
    }

    return true;
  };

  // GOOGLE SIGN-IN WITH POPUP (COOP-SAFE) -------------------------------------------
  const googleSignIn = async (
    selectedType?: "jobseeker" | "hr" | "admin"
  ): Promise<boolean> => {
    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });


      // Use popup method with proper error handling
      const result = await signInWithPopup(auth, provider);
      const fbUser = result.user;

      if (!fbUser) {
        throw new Error("No user returned from Google authentication");
      }


      // Handle user creation/validation
      const success = await handleGoogleUser(fbUser, selectedType);

      if (success) {
        toast.success("Successfully signed in with Google!");
        setIsLoading(false);
        return true;
      } else {
        setIsLoading(false);
        return false;
      }
    } catch (error: any) {

      // Handle specific error cases
      if (error.code === "auth/popup-closed-by-user") {
        toast.error("Sign-in was cancelled. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        toast.error(
          "Popup was blocked by your browser. Please allow popups and try again."
        );
      } else if (error.code === "auth/cancelled-popup-request") {
        toast.error("Sign-in was cancelled. Please try again.");
      } else if (error.code === "auth/network-request-failed") {
        toast.error(
          "Network error. Please check your connection and try again."
        );
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Too many attempts. Please wait a moment and try again.");
      } else if (error.message?.includes("Cross-Origin-Opener-Policy")) {
        toast.error(
          "Browser security settings are blocking sign-in. Please try refreshing the page."
        );
      } else {
        toast.error(
          "Google sign-in failed. Please try again or use email/password."
        );
      }

      setIsLoading(false);
      return false;
    }
  };

  // Function to update user profile in context (called when profile is updated)
  const updateUserProfile = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem("vetriaai_user", JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, googleSignIn, logout, isLoading, updateUserProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};
